/**
 * Attributes that mark an element as the page's "highest priority content" (HPC).
 *
 * The web-vitals tooling measures paint timing for the element carrying
 * `data-hpc`, and the Container Timing observer reads the matching
 * `containertiming="hpc"`. The two attributes always travel together, so this
 * helper emits both at once — replacing the build-time transform that used to
 * mirror `data-hpc` onto `containertiming`.
 */
export interface HpcProps {
  'data-hpc': true
  containertiming: 'hpc'
}

export interface HpcOptions {
  enabled: boolean
}

// Stable, frozen singletons — `hpc()` is called on nearly every page, so we
// hand back the same object references instead of allocating on each call.
const HPC_PROPS: HpcProps = Object.freeze({'data-hpc': true, containertiming: 'hpc'})
const EMPTY_PROPS: Record<string, never> = Object.freeze({})

/**
 * Returns the attributes that mark an element as the page's highest priority
 * content for paint-timing measurement. Spread the result onto the JSX element
 * that wraps the page's primary content.
 *
 * @example
 * <div {...hpcAttrs()}>…</div>
 *
 * @example
 * // Conditionally mark an element (e.g. only the first item in a list):
 * <div {...hpcAttrs({enabled: index === 0})}>…</div>
 */
export function hpcAttrs(options?: HpcOptions): HpcProps | Record<string, never> {
  // Omitted options mean "enabled" — avoids allocating a default object on every call.
  return !options || options.enabled ? HPC_PROPS : EMPTY_PROPS
}
